import React, { useMemo } from 'react';

import {
  useLinkDivisionHandleMutation,
  useListCarriersQuery,
  useListDivisionHandlesQuery,
  useUnlinkDivisionHandleMutation,
} from '@/services/api/carriersApiService';

import type { ContactHandle } from './types';

interface DivisionHandlesDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  carrierId: string;
  divisionId: string;
  divisionName: string;
}

export const DivisionHandlesDrawer = ({
  isOpen,
  onClose,
  carrierId,
  divisionId,
  divisionName,
}: DivisionHandlesDrawerProps) => {
  const { data: carriers } = useListCarriersQuery();
  const { data: linkedHandles = [] } = useListDivisionHandlesQuery(
    {
      carrierId,
      divisionId,
    },
    { skip: !carrierId || !divisionId },
  );
  const [linkHandle] = useLinkDivisionHandleMutation();
  const [unlinkHandle] = useUnlinkDivisionHandleMutation();

  const allHandles: ContactHandle[] = useMemo(() => {
    const carrier = (carriers || []).find((c) => c.id === carrierId);
    return carrier ? carrier.handles : [];
  }, [carriers, carrierId]);

  const linkedIds = new Set(linkedHandles.map((h) => h.id));

  const handleToggle = async (handleId: string, checked: boolean) => {
    try {
      if (checked) {
        await linkHandle({ carrierId, divisionId, handleId }).unwrap();
      } else {
        await unlinkHandle({ carrierId, divisionId, handleId }).unwrap();
      }
    } catch (e) {
      // no-op toast; parent page likely has toasts
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-black/30"
        role="button"
        tabIndex={0}
        aria-label="Close division handles"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
      />
      <div className="absolute right-0 top-0 h-full w-[480px] bg-white shadow-xl border-l border-gray-200 p-4 flex flex-col">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {divisionName} — Handles
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            Close
          </button>
        </div>
        <div className="mt-4 overflow-auto">
          {allHandles.length === 0 ? (
            <div className="text-sm text-gray-500">No carrier handles</div>
          ) : (
            <div className="space-y-2">
              {allHandles.map((h) => {
                const isChecked = linkedIds.has(h.id);
                return (
                  <label
                    key={h.id}
                    htmlFor={`div-handle-${h.id}`}
                    className="flex items-center gap-2 p-2 rounded hover:bg-gray-50"
                  >
                    <input
                      id={`div-handle-${h.id}`}
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => handleToggle(h.id, e.target.checked)}
                    />
                    <span className="text-sm text-gray-900">
                      {h.name || h.handleType}
                    </span>
                    <span className="text-xs text-gray-600 truncate">
                      {h.handleType === 'MAILING_ADDRESS'
                        ? `${h.addressLine1 || ''} ${h.city || ''}`
                        : h.value || ''}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
